import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { Shield, Plus, Copy, Trash2, Loader2, AlertCircle, CheckCircle2, Clock, Users } from 'lucide-react';
import { apiClient } from '../../api/client';

const AdminDemoCodes = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [maxUses, setMaxUses] = useState('');
  const [error, setError] = useState(null);
  const [justCreated, setJustCreated] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.request('/api/admin/demo-codes');
      setCodes(res.codes || []);
    } catch (err) {
      setError(err?.message || 'Failed to load codes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createCode = async (e) => {
    e?.preventDefault?.();
    if (!label.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const body = {
        label: label.trim(),
        expires_in_days: parseInt(expiresInDays, 10),
      };
      if (maxUses && parseInt(maxUses, 10) > 0) body.max_uses = parseInt(maxUses, 10);
      const res = await apiClient.request('/api/admin/demo-codes', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setJustCreated(res);
      setLabel('');
      setMaxUses('');
      await load();
    } catch (err) {
      setError(err?.message || 'Failed to create code');
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (codeId) => {
    if (!confirm('Revoke this passcode? Anyone currently using it will be locked out on their next verify.')) return;
    try {
      await apiClient.request(`/api/admin/demo-codes/${codeId}`, { method: 'DELETE' });
      await load();
    } catch (err) {
      setError(err?.message || 'Failed to revoke');
    }
  };

  const copy = async (code, id) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {}
  };

  const formatExpiry = (iso) => {
    const d = new Date(iso);
    const hours = (d - new Date()) / 36e5;
    if (hours < 0) return 'expired';
    if (hours < 24) return `in ${Math.max(1, Math.round(hours))}h`;
    return `in ${Math.round(hours / 24)}d`;
  };

  return (
    <AdminLayout>
      <div className="p-6 space-y-6 max-w-5xl" data-testid="admin-demo-codes-page">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center text-purple-600">
            <Shield size={20} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Demo Access Codes</h1>
            <p className="text-sm text-gray-500">
              Issue time-limited passcodes for schools, parents, and investors to trial the demo teen account.
              Each use is logged.
            </p>
          </div>
        </div>

        {/* Demo account credentials */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-sm font-semibold text-amber-900 mb-1">Demo Account Credentials</p>
          <p className="text-xs text-amber-800 mb-2">
            Give these to the stakeholder along with their passcode:
          </p>
          <div className="font-mono text-xs space-y-0.5 bg-white/60 rounded-lg p-3">
            <div>Login URL: <span className="font-semibold">https://hibff.com/login</span></div>
            <div>Email:     <span className="font-semibold">demo.teen@hibff.com</span></div>
            <div>Password:  <span className="font-semibold">dgBVRCP0uAGc9w</span></div>
          </div>
        </div>

        {/* Create form */}
        <form onSubmit={createCode} className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <Plus size={14} /> Issue a new passcode
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-xs text-gray-600 mb-1">
                Recipient label <span className="text-gray-400">(required)</span>
              </label>
              <input
                value={label}
                onChange={e => setLabel(e.target.value)}
                placeholder="e.g. Lincoln High — Jane Doe, Series-A investor"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                data-testid="demo-code-label-input"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Expires in (days)</label>
              <input
                type="number" min={1} max={90}
                value={expiresInDays}
                onChange={e => setExpiresInDays(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">
                Max uses <span className="text-gray-400">(blank = unlimited)</span>
              </label>
              <input
                type="number" min={1}
                value={maxUses}
                onChange={e => setMaxUses(e.target.value)}
                placeholder="unlimited"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
              />
            </div>
          </div>
          {error && (
            <div className="flex items-start gap-2 p-2.5 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
              <AlertCircle size={14} className="flex-shrink-0 mt-0.5" /> {error}
            </div>
          )}
          <button
            type="submit"
            disabled={creating || !label.trim()}
            data-testid="demo-code-create-btn"
            className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-500 text-white text-sm font-semibold rounded-lg disabled:opacity-50 hover:shadow-lg flex items-center gap-2"
          >
            {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Create passcode
          </button>
          {justCreated && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
              <CheckCircle2 size={16} className="text-green-600" />
              <span className="text-gray-700">New passcode:</span>
              <code className="font-mono font-bold text-green-700 bg-white px-2 py-0.5 rounded border border-green-200">{justCreated.code}</code>
              <button type="button" onClick={() => copy(justCreated.code, 'just-created')} className="text-xs text-purple-600 hover:underline">
                {copiedId === 'just-created' ? 'Copied!' : 'Copy'}
              </button>
            </div>
          )}
        </form>

        {/* Codes list */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
            <h2 className="text-sm font-semibold text-gray-900">Issued codes</h2>
            <span className="text-xs text-gray-500">({codes.length})</span>
          </div>
          {loading ? (
            <div className="p-6 flex items-center gap-2 text-sm text-gray-500">
              <Loader2 size={14} className="animate-spin" /> Loading…
            </div>
          ) : codes.length === 0 ? (
            <div className="p-6 text-sm text-gray-500 text-center">
              No codes yet. Issue one above.
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {codes.map(c => (
                <div key={c.code_id} className="px-5 py-3 flex items-center gap-3 text-sm" data-testid={`demo-code-row-${c.code_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <code className="font-mono font-bold text-gray-900">{c.code}</code>
                      <button onClick={() => copy(c.code, c.code_id)} className="text-gray-400 hover:text-purple-600" title="Copy">
                        <Copy size={12} />
                      </button>
                      {copiedId === c.code_id && <span className="text-[10px] text-green-600">Copied</span>}
                      {c.revoked && <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded">Revoked</span>}
                      {!c.revoked && c.expired && <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">Expired</span>}
                      {c.active && <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded">Active</span>}
                    </div>
                    <div className="text-xs text-gray-500 truncate">{c.label}</div>
                    <div className="text-[11px] text-gray-400 flex items-center gap-3 mt-0.5">
                      <span className="flex items-center gap-1"><Clock size={10} /> Expires {formatExpiry(c.expires_at)}</span>
                      <span className="flex items-center gap-1"><Users size={10} /> {c.use_count} use{c.use_count === 1 ? '' : 's'}{c.max_uses ? ` / ${c.max_uses}` : ''}</span>
                    </div>
                  </div>
                  {!c.revoked && (
                    <button
                      onClick={() => revoke(c.code_id)}
                      title="Revoke"
                      data-testid={`demo-code-revoke-${c.code_id}`}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDemoCodes;
